import type { HeatGridCameraResponse, HeatGridPart, ObserverTrackSide } from '@repo/frontend-types';
import { decodeHeatGridCells, type HeatGridCells } from '@repo/front-end-utils';

/**
 * What the Observers step's heat shows (`observers-heat-follows-tracked-part`, R1–R2): one stored
 * body part, or the whole body.
 */
export type HeatView = HeatGridPart | 'WHOLE_BODY';

/**
 * The stored parts each view sums.
 *
 * The daily bin function (`heat_grid_bin_day`) stores HEAD (COCO keypoints 0–4), TORSO (5, 6, 11,
 * 12), HIPS (11, 12), LEGS (13–16) and FEET (15, 16). The parts overlap — HIPS sits inside TORSO and
 * FEET inside LEGS — and the elbows and wrists (7–10) are in no part. So the whole body is HEAD +
 * TORSO + LEGS: 13 of the 17 keypoints, each counted exactly once. Adding HIPS or FEET on top would
 * count those keypoints twice.
 *
 * That is the whole body by design, not an approximation (Mike, 2026-10-22): elbows and wrists are
 * deliberately excluded from Whole body everywhere, including how a whole-body observer is read.
 */
export const HEAT_VIEW_PARTS: Record<HeatView, readonly HeatGridPart[]> = {
  HEAD: ['HEAD'],
  TORSO: ['TORSO'],
  HIPS: ['HIPS'],
  LEGS: ['LEGS'],
  FEET: ['FEET'],
  WHOLE_BODY: ['HEAD', 'TORSO', 'LEGS'],
};

/** How each view is named in the caption, the chip, the image and the switch's description. */
export const HEAT_VIEW_WORD: Record<HeatView, string> = {
  FEET: 'Feet',
  HIPS: 'Hips',
  TORSO: 'Torso',
  LEGS: 'Legs',
  HEAD: 'Head',
  WHOLE_BODY: 'Whole body',
};

/**
 * The heat a tracked part is shown with: each stored part as itself, and Whole body as the sum
 * above. An unrecognised value (a newer keypoint this build cannot name) reads as Torso, which is
 * what a stored observer with no tracked part counts as (`DEFAULT_TRACK_SIDE`).
 */
export const heatViewForTrackSide = (side: ObserverTrackSide): HeatView => {
  switch (side) {
    case 'HEAD':
    case 'TORSO':
    case 'HIPS':
    case 'LEGS':
    case 'FEET':
    case 'WHOLE_BODY':
      return side;
    default:
      return 'TORSO';
  }
};

/** Keypoints counted for a view across the window: its parts' totals summed. */
export const heatViewTotal = (value: HeatGridCameraResponse, view: HeatView): number =>
  HEAT_VIEW_PARTS[view].reduce((sum, part) => sum + (value.parts[part]?.total ?? 0), 0);

/** Cells summed cell by cell: one output cell per `(cx, cy)`, in first-seen order. */
export const sumHeatGridCells = (grids: readonly HeatGridCells[]): HeatGridCells => {
  if (grids.length === 1) return grids[0];
  const at = new Map<number, number>();
  const xs: number[] = [];
  const ys: number[] = [];
  const ns: number[] = [];
  for (const grid of grids) {
    for (let i = 0; i < grid.n.length; i += 1) {
      // int16 coordinates, so one 32-bit key per cell.
      const key = ((grid.cx[i] & 0xffff) << 16) | (grid.cy[i] & 0xffff);
      const found = at.get(key);
      if (found === undefined) {
        at.set(key, ns.length);
        xs.push(grid.cx[i]);
        ys.push(grid.cy[i]);
        ns.push(grid.n[i]);
      } else {
        ns[found] += grid.n[i];
      }
    }
  }
  return { cx: Int16Array.from(xs), cy: Int16Array.from(ys), n: Int32Array.from(ns) };
};

/**
 * Answers for each of a view's parts merged into one: the parts are read from the same camera and
 * window, so everything but `parts` is the same in each (the first one's is kept).
 */
export const mergeHeatPartAnswers = (
  answers: readonly HeatGridCameraResponse[],
): HeatGridCameraResponse => ({
  ...answers[0],
  parts: Object.assign({}, ...answers.map(a => a.parts)),
});

/**
 * A view's cells, decoded and summed; null when it has no heat (the caption says so, and no blank
 * image claims otherwise). Throws when any part's cells cannot be decoded, so half a whole body is
 * never drawn as if it were all of it.
 */
export const decodeHeatView = (
  value: HeatGridCameraResponse,
  view: HeatView,
): HeatGridCells | null => {
  const grids: HeatGridCells[] = [];
  for (const part of HEAT_VIEW_PARTS[view]) {
    const cells = value.parts[part];
    if (!cells || cells.total === 0 || cells.cells === 0) continue;
    grids.push(decodeHeatGridCells(cells.encoding, cells.data, cells.cells));
  }
  return grids.length ? sumHeatGridCells(grids) : null;
};
